import { useEffect, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Label from '../label/Label';
import Icons from '../../icons/Icons';
import './styles.scss';
import moment from 'moment';
import { ErrorMessage } from 'formik';

const formatTimeString = (date) => moment(date).format('hh:mm');

const CustomDatePicker = ({
  label = '',
  iconName = 'calenderIcon',
  name = '',
  isRequired = false,
  value = null,
  onChangeCb = () => {},
  placeholder = '',
  disabled = false,
  customClass = '',
  minDate,
  maxDate,
  showMonthDropdown = true,
  showYearDropdown = true,
  onFocusCb = () => {},
  onMonthChange = () => {},
}) => {
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
  const [timeString, setTimeString] = useState('');
  const [ampm, setAmPm] = useState('AM');
  const dateInputRef = useRef(null);
  const [timeError, setTimeError] = useState('');


  useEffect(() => {
    if (value) {
      const date = new Date(value);
      setSelectedDate(date);
      setTimeString(formatTimeString(date));
      setAmPm(moment(date).format('A'));
    }
  }, [value]);

  const updateDateTime = (dateObj, timeStr, ampmStr) => {
  const timeMoment = moment(`${timeStr} ${ampmStr}`, 'hh:mm A');
  if (!timeMoment.isValid()) return;

  const localDateTime = moment(dateObj)
    .set({ hour: timeMoment.hours(), minute: timeMoment.minutes(), second: 0, millisecond: 0 });

  setSelectedDate(localDateTime.toDate());
  setTimeString(timeStr);
  setAmPm(ampmStr);

  const formattedDateTime = localDateTime.format('YYYY-MM-DDTHH:mm:ss'); 
  onChangeCb(formattedDateTime); 
};


  const handleDateChange = (date) => {
    const now = new Date();
    const defaultTime = selectedDate || now;
    const formattedTime = formatTimeString(defaultTime);
    const formattedAmPm = moment(defaultTime).format('A');

    updateDateTime(date, formattedTime, formattedAmPm);
  };

  const handleTimeChange = (e) => {
    const newTime = e.target.value.trim();
    const timeRegex = /^(0?[1-9]|1[0-2]):([0-5][0-9])$/;

    const [hourStr, minuteStr] = newTime.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    
    if (!timeRegex.test(newTime)) {
      setTimeString('12:00');
      setAmPm('PM'); 
      setTimeError('Enter time in 12-hour format (hh:mm)');
      if (selectedDate) {
        updateDateTime(selectedDate, '12:00', 'PM');
      }
      return;
    }


    if (hour > 12 || minute > 59) {
      setTimeString('12:00');
      setAmPm('PM');
      setTimeError('Enter time in 12-hour format (hh:mm)');
      if (selectedDate) {
        updateDateTime(selectedDate, '12:00', 'PM');
      }
      return;
    }

    setTimeError('');
    setTimeString(newTime);
    if (selectedDate) {
      updateDateTime(selectedDate, newTime, ampm);
    }
  };



  return (
    <div className="flex flex-col">
      <div>
        <Label fontWeight="bold" isRequired={isRequired}>
          {label}
        </Label>
      </div>

      <div className="flex gap-2 items-center">
        <div className="relative w-1/2">
        <button
            type="button"
            onClick={() => {
              if (dateInputRef.current) {
                dateInputRef.current.setFocus(); 
              }
            }}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10"
          >
          <Icons iconName={iconName} style={{ width: 20, height: 20 }} />
          </button>
          <ReactDatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            placeholderText={placeholder}
            dateFormat="dd/MM/yyyy"
            className={`met-scrollbar custom-datepicker block opacity-100 w-full p-7px text-base rounded-md outline-none pl-30px ${customClass}`}
            icon={<Icons style={{ position: 'absolute', margin: '5px' }} iconName={iconName} />}
            minDate={minDate}
            maxDate={maxDate}
            showMonthDropdown={showMonthDropdown}
            showYearDropdown={showYearDropdown}
            onMonthChange={onMonthChange}
            dropdownMode="scroll"
            disabled={disabled}
            toggleCalendarOnIconClick
            onKeyDown={(e) => e.preventDefault()}
            autoComplete="off"
            onFocus={onFocusCb}
            ref={dateInputRef}
            name={name}
          />
        </div>

        
        <div className="w-1/2 flex items-center gap-2 relative">
          <input
          type="text"
          value={timeString}
          placeholder="hh:mm"
          onChange={(e) => setTimeString(e.target.value)}
          onBlur={handleTimeChange}
          disabled={disabled}
          className={"custom-datepicker w-full p-7px text-base rounded-md outline-none border border-gray-300"}
        />
        <div className="flex items-center h-[30px] max-w-xs rounded-md overflow-hidden border border-gray-300 absolute right-[6px] w-[70px]">
          <button
            type="button"
            onClick={() => setAmPm("AM")}
            className={`w-1/2 py-2 text-sm font-medium ${
              ampm === "AM"
                ? "bg-met-primary text-white"
                : "bg-white text-gray-700 hover:bg-gray-100"
            } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
            disabled={disabled}
          >
            AM
          </button>
          <button
            type="button"
            onClick={() => setAmPm("PM")}
            className={`w-1/2 py-2 text-sm font-medium ${
              ampm === "PM"
                ? "bg-met-primary text-white"
                : "bg-white text-gray-700 hover:bg-gray-100"
            } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
            disabled={disabled}
          >
            PM
          </button>
        </div>
        </div>
      </div>
      {name && (
        <div className="mt-1">
          <ErrorMessage name={name} component="div" className="error-text" />
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;